import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import { validateEmail, validatePhone } from '../../../util/validation';
import Countries from '../../../util/countries';
import CountryPicker from '../../country-select';
import ErrorPopup from '../../error';
import AgeGage from '../../age-cage';
import Log from '../../../util/log';
import ensureTokenPosition from '../../../util/ensure-token-position';

export default class Presenter {
  constructor(model, config, onLogin) {
    this.model = model;
    this.facebookAppId = config.facebook_app_id;
    this.googleAppId = config.google_app_id;
    this.outsideAppId = config.outside_app_id;
    this.oauthOnly = config.oauth_only;
    this.config = config.login;
    this.age = config.age;
    this.token = config.token;
    this.onLogin = onLogin;
    this.errorConfig = config.error || {};
    this.defaultCampaign = config.login.default_campaign;
    this.authButtonOrder = config.auth_button_order;
    this.samsungAppId = config.samsung_app_id;
    this.samsungAliasId = config.samsung_alias_id;
  }

  async onAttach() {
    const { allow_phone: allowPhone, allow_email: allowEmail, regions } = this.token;
    this.model.allowPhone = allowPhone;
    this.model.allowEmail = allowEmail;
    this.model.showFacebook = !!this.facebookAppId;
    this.model.showGoogle = !!this.googleAppId;
    this.model.showSamsung = !!this.samsungAppId && !!this.samsungAliasId;
    this.model.showOutsideOauth = !!this.outsideAppId;
    this.model.oauthOnly = !!this.oauthOnly;
    this.model.authButtonOrder = ensureTokenPosition(this.authButtonOrder);

    if (this.age && this.age.min) {
      AgeGage.show(
        this.age.format,
        this.age.min,
        this.age.redirect,
        this.age.delay || 5000,
        () => {}
      );
    }
    let defaultCountry = {
      code: 'us',
      phonePrefix: '+1',
      name: 'United States',
    };
    if (regions?.length > 0) {
      const result = (await Countries.get().catch(() => [])).find(
        (country) => country.code.toLowerCase() === regions[0].toLowerCase()
      );
      if (result) {
        defaultCountry = result;
      }
    }

    let detected = (await Countries.detect().catch(() => {})) || defaultCountry;
    if (!regions.find((country) => detected.code.toLowerCase() === country.toLowerCase())) {
      detected = defaultCountry;
    }
    if (regions?.length === 1) {
      this.model.disableCountryPicker = true;
    }
    this.selectedCountry = detected;
    this.model.countryCode = this.selectedCountry.phonePrefix;
  }

  onInitialTokenChange(initialToken) {
    this.model.inputValue = initialToken;
  }

  onInputChange(value) {
    const { allow_phone: allowPhone } = this.token;
    this.model.inputValue = value;
    if (allowPhone && validatePhone(value) && value.indexOf('+') !== 0) {
      this.model.showCountryCode = true;
    } else {
      this.model.showCountryCode = false;
    }
  }

  onFacebookClick() {
    this.onNextClick('facebook');
  }

  onGoogleClick() {
    this.onNextClick('google');
  }

  onOutsideClick() {
    this.onNextClick('outside');
  }

  onSamsungClick() {
    this.onNextClick('samsung');
  }

  onNextClick(continueWith) {
    let value = this.model.inputValue;
    value = value?.trim();
    const { allow_phone: allowPhone, allow_email: allowEmail } = this.token;

    const { delay: errorDelay, redirect_on_go: redirectOnGoError } = this.errorConfig;

    if (
      continueWith !== 'facebook' &&
      continueWith !== 'google' &&
      continueWith !== 'outside' &&
      continueWith !== 'samsung'
    ) {
      if (allowPhone && allowEmail) {
        if (!validateEmail(value) && !validatePhone(value)) {
          this.model.errorText = 'login.landing.error_invalid_email_phone_number';
          return;
        }
      } else if (allowEmail) {
        if (!validateEmail(value)) {
          this.model.errorText = 'login.landing.error_invalid_email';
          return;
        }
      } else if (allowPhone) {
        if (!validatePhone(value)) {
          this.model.errorText = 'login.landing.error_invalid_phone_number';
          return;
        }
      }
      const isEmail = value.indexOf('@') !== -1;
      if (!isEmail) {
        try {
          const phoneUtil = PhoneNumberUtil.getInstance();

          const phoneNumber = phoneUtil.parse(value, this.selectedCountry.code);
          const token = phoneUtil.format(phoneNumber, PhoneNumberFormat.E164).trim();
          if (!phoneUtil.isValidNumber(phoneNumber)) {
            this.model.errorText = 'claim.landing.error_invalid_phone_number';
            return;
          }

          if (!token.startsWith(this.selectedCountry.phonePrefix)) {
            this.model.errorText = 'login.landing.error_invalid_region';
            return;
          }

          if (token.length > 0) {
            value = token;
          }
        } catch (error) {
          Log.error(error);
          this.model.errorText = 'login.landing.error_invalid_phone_number';
          return;
        }
      }
    }

    const params = { user: value };
    if (continueWith === 'facebook') {
      params.connect = 'facebook';
    } else if (continueWith === 'google') {
      params.connect = 'google';
    } else if (continueWith === 'outside') {
      params.connect = 'outside';
    } else if (continueWith === 'samsung') {
      params.connect = 'samsung';
    }

    this.model.errorText = null;
    this.model.loading = true;
    VlabsUser.go(
      '/login',
      params,
      (_, notificationType) => {
        this.model.loading = false;
        this.onLogin?.(notificationType);
      },
      (error) => {
        this.model.loading = false;
        // Check for user_not_found
        if (error.code === 150) {
          this.model.errorText = 'login.landing.error_account_does_not_exist';
        } else {
          ErrorPopup.show(error, () => {}, redirectOnGoError && errorDelay);
        }
        Log.error(error);
      }
    );
  }

  onCountryCodeClick() {
    const { regions } = this.token;
    CountryPicker.show(this.selectedCountry, regions, null, (selected) => {
      if (selected) {
        this.selectedCountry = selected;
        this.model.countryCode = this.selectedCountry.phonePrefix;
      }
    });
  }

  onSignUpClick() {
    Log.info('onSignUpClick', this.defaultCampaign);
    if (this.defaultCampaign) {
      const currentURL = window.location.href;
      const urlObject = new URL(currentURL);

      // Get the existing path segments
      const pathSegments = urlObject.pathname.split('/').filter((segment) => segment);

      // Add the new path segment
      pathSegments.push(this.defaultCampaign);

      // Construct the new path
      urlObject.pathname = `/${pathSegments.join('/')}`;

      // Remove search params
      urlObject.search = '';

      // Use window.location.replace to navigate to the modified URL
      window.location.replace(urlObject);
    }
  }
}
